import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { IOutcomeCoverage, ISubtreeRollup } from '@stem/contracts';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';
import { EmptyState, Meter, Section, Stat } from './parts';

/**
 * The institutional dashboard: a ministry, a county, an NGO programme office.
 *
 * Every number here comes from one endpoint that a head teacher also calls. The
 * difference is not the query — it is `analytics@read_subtree` in the caller's
 * grant, which sets `app.org_subtree` on the transaction and lets the RLS
 * policy match `org_path <@ scope` instead of `org_id = :org`. One predicate,
 * one GiST index scan, forty-seven counties.
 *
 * The coverage table underneath is the part that sells this tier. "Which CBC
 * outcomes has this county actually covered" cannot be answered from course
 * completions — it needs content tagged to outcomes, which is why
 * `content_outcomes` exists from the first migration.
 */
export function MinistryDashboard() {
  const [curriculum, setCurriculum] = useState('KE-CBC');
  const [grade, setGrade] = useState('grade-7');
  const canExport = useSession(
    state => state.permissions.includes('analytics@export') || state.permissions.includes('*@*'),
  );

  const rollup = useQuery({
    queryKey: ['rollup'],
    queryFn: () => apiClient.get<ISubtreeRollup>('/v1/analytics/rollup'),
  });

  const coverage = useQuery({
    queryKey: ['coverage', curriculum, grade],
    queryFn: () =>
      apiClient.get<IOutcomeCoverage[]>(
        `/v1/analytics/coverage/${curriculum}?grade=${encodeURIComponent(grade)}`,
      ),
  });

  const totals = rollup.data?.totals;
  const covered = (coverage.data ?? []).filter(row => row.contentCount > 0).length;

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">National dashboard</h1>
      <p className="text-sm text-ink-600">
        Every organisation beneath{' '}
        <span className="font-mono text-xs">{rollup.data?.scope ?? '…'}</span> in the tree.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label="Organisations" value={rollup.data?.byOrg.length ?? '—'} />
        <Stat label="Educators & learners" value={totals?.members ?? '—'} />
        <Stat label="Enrolments" value={totals?.enrollments ?? '—'} />
        <Stat
          label="Completion rate"
          value={totals ? `${totals.completionRate}%` : '—'}
          tone={(totals?.completionRate ?? 0) >= 60 ? 'good' : 'warn'}
        />
        <Stat label="Laboratory sessions" value={totals?.labRuns ?? '—'} />
      </div>

      <Section title="By organisation">
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr>
                <th className="px-4 py-2">Organisation</th>
                <th className="px-4 py-2">Members</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2">Enrolments</th>
                <th className="px-4 py-2">Completion</th>
                <th className="px-4 py-2">Certificates</th>
                <th className="px-4 py-2">Labs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {(rollup.data?.byOrg ?? []).map(row => (
                <tr key={row.orgId}>
                  <td className="px-4 py-2">{row.orgName}</td>
                  <td className="px-4 py-2 tabular-nums">{row.members}</td>
                  <td className="px-4 py-2 tabular-nums">{row.activeLearners}</td>
                  <td className="px-4 py-2 tabular-nums">{row.enrollments}</td>
                  <td className="px-4 py-2">
                    <Meter value={row.completionRate} />
                  </td>
                  <td className="px-4 py-2 tabular-nums">{row.certificatesIssued}</td>
                  <td className="px-4 py-2 tabular-nums">{row.labRuns}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Curriculum coverage">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
          <input
            className="input max-w-[10rem]"
            value={curriculum}
            onChange={event => setCurriculum(event.target.value)}
            aria-label="Curriculum code"
          />
          <input
            className="input max-w-[10rem]"
            value={grade}
            onChange={event => setGrade(event.target.value)}
            aria-label="Grade band"
          />
          <span className="text-xs text-ink-500">
            {covered} of {coverage.data?.length ?? 0} outcomes have content
          </span>

          {canExport && (
            <a
              className="btn-secondary ml-auto"
              href="/api/v1/analytics/export.csv"
              target="_blank"
              rel="noreferrer"
            >
              Export CSV
            </a>
          )}
        </div>

        {coverage.data && coverage.data.length > 0 ? (
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Outcome</th>
                  <th className="px-4 py-2">Content</th>
                  <th className="px-4 py-2">Engaged</th>
                  <th className="px-4 py-2">Completed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {coverage.data.map(row => (
                  <tr key={row.outcomeId} className={row.contentCount === 0 ? 'bg-red-50/60' : ''}>
                    <td className="px-4 py-2 font-mono text-xs">{row.code}</td>
                    <td className="px-4 py-2">{row.statement}</td>
                    <td className="px-4 py-2 tabular-nums">{row.contentCount}</td>
                    <td className="px-4 py-2 tabular-nums">{row.learnersEngaged}</td>
                    <td className="px-4 py-2 tabular-nums">{row.learnersCompleted}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState>
            No outcomes found for {curriculum} {grade}. Install a curriculum pack, or check the
            curriculum code.
          </EmptyState>
        )}
      </Section>
    </div>
  );
}
